import { useMutation, useQueryClient } from "react-query";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IBreadCrumbData } from "../../components/bread-crumb/BreadCrumbUtils";
import { RouteContextType } from "../../hooks/use-route-context/UseRouteContext";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import OfflineDataService from "../../services/offline-data-service/OfflineDataService";
import { RestError } from "../../services/rest-service/RestService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";

import { keys } from "./Keys";

export type UseUpdateBreadcrumbParameters = {
    customerID: number,
    viewID: UIViewEnum,
    routeContext: RouteContextType,
    offlineDataLoaded: boolean,
    offline: boolean
} & BaseUseMutationParameters<void, INavigationItem>;

type MutationContextType = {
    previousBreadcrumbData: IBreadCrumbData | undefined
};

export function useUpdateBreadCrumb({
    customerID,
    viewID,
    routeContext,
    offlineDataLoaded,
    offline,
    onError,
    onSuccess
}: UseUpdateBreadcrumbParameters) {
    const queryClient = useQueryClient();

    return useMutation<void, RestError, INavigationItem, MutationContextType>(
        (data) => {
            return Promise.resolve();
        },
        {
            onMutate: async (item: INavigationItem) => {

                const current = await OfflineDataService.getObject(customerID,
                                                routeContext.contextObject as IeLynxObject);

                if (current) {
                    OfflineDataService.updateObject(customerID, routeContext.contextObject as IeLynxObject, current); 
                }                                                

                const previousData = queryClient.getQueryData<IBreadCrumbData>(
                    keys.data(customerID,
                        viewID,
                        routeContext,
                        offlineDataLoaded,
                        offline)
                );

                const updatedData = {...previousData,
                    breadcrumbItems: [...previousData?.breadcrumbItems as INavigationItem[]],
                    contextObjectName: item.title
                } as IBreadCrumbData;

                const ndx = updatedData.breadcrumbItems.findIndex(o => o.objectID === item.objectID && o.objectTypeID === item.objectTypeID);
                updatedData.breadcrumbItems.splice(ndx, 1, item);

                queryClient.setQueryData<IBreadCrumbData>(
                    keys.data(
                        customerID,
                        viewID,
                        routeContext,
                        offlineDataLoaded,
                        offline
                    ),
                    (old?: IBreadCrumbData) => {
                        return updatedData;
                    }
                );

                return {
                    previousBreadcrumbData: previousData
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _breadcrumbData, context) => {

                if (error && context?.previousBreadcrumbData) {
                    queryClient.setQueryData<IBreadCrumbData>(
                        keys.data(
                            customerID,
                            viewID,
                            routeContext,
                            offlineDataLoaded,
                            offline
                            ),
                        context.previousBreadcrumbData
                    );
                }

                queryClient.invalidateQueries(keys.all);
            }
        }
    );
}
